import { Injectable } from '@angular/core';

export interface AppTheme {
  id: string;
  name: string;
  primary: string;
  primaryHover: string;
  primaryDark: string;
  primaryDarkest: string;
  primaryLight: string;
  rgb: string;
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly STORAGE_KEY = 'appTheme';

  readonly themes: AppTheme[] = [
    { id: 'teal',   name: 'Teal',   primary: '#008080', primaryHover: '#006e6e', primaryDark: '#006666', primaryDarkest: '#004d4d', primaryLight: '#00b3b3', rgb: '0, 128, 128' },
    { id: 'ocean',  name: 'Ocean',  primary: '#1565C0', primaryHover: '#1159b0', primaryDark: '#0D47A1', primaryDarkest: '#0a3680', primaryLight: '#4d93e8', rgb: '21, 101, 192' },
    { id: 'coral',  name: 'Coral',  primary: '#f96332', primaryHover: '#e5552e', primaryDark: '#d44e22', primaryDarkest: '#b03d18', primaryLight: '#fb8b6a', rgb: '249, 99, 50' },
    { id: 'indigo', name: 'Indigo', primary: '#5C6BC0', primaryHover: '#505eb0', primaryDark: '#3949AB', primaryDarkest: '#2c3886', primaryLight: '#8c99d6', rgb: '92, 107, 192' },
    { id: 'forest', name: 'Forest', primary: '#388E3C', primaryHover: '#308034', primaryDark: '#2E7D32', primaryDarkest: '#1b5e20', primaryLight: '#66bb6a', rgb: '56, 142, 60' },
    { id: 'rose',   name: 'Rose',   primary: '#D81B60', primaryHover: '#c41557', primaryDark: '#AD1457', primaryDarkest: '#880e4f', primaryLight: '#f06292', rgb: '216, 27, 96' },
  ];

  currentThemeId: string = 'teal';

  get currentPrimary(): string     { return this.themes.find(t => t.id === this.currentThemeId)?.primary      ?? '#008080'; }
  get currentPrimaryHover(): string { return this.themes.find(t => t.id === this.currentThemeId)?.primaryHover ?? '#006e6e'; }
  get currentPrimaryDark(): string  { return this.themes.find(t => t.id === this.currentThemeId)?.primaryDark  ?? '#006666'; }
  get currentPrimaryLight(): string { return this.themes.find(t => t.id === this.currentThemeId)?.primaryLight ?? '#00b3b3'; }

  loadTheme(): void {
    const saved = localStorage.getItem(this.STORAGE_KEY) || 'teal';
    this.applyTheme(saved);
  }

  applyTheme(themeId: string): void {
    const theme = this.themes.find(t => t.id === themeId) ?? this.themes[0];
    this.currentThemeId = theme.id;
    localStorage.setItem(this.STORAGE_KEY, theme.id);
    this.setCssVars(theme);
  }

  private setCssVars(t: AppTheme): void {
    const r = document.documentElement;
    const rgb = t.rgb;
    r.style.setProperty('--theme-primary',         t.primary);
    r.style.setProperty('--theme-primary-hover',   t.primaryHover);
    r.style.setProperty('--theme-primary-dark',    t.primaryDark);
    r.style.setProperty('--theme-primary-darkest', t.primaryDarkest);
    r.style.setProperty('--theme-primary-light',   t.primaryLight);
    r.style.setProperty('--theme-primary-a03',     `rgba(${rgb}, 0.03)`);
    r.style.setProperty('--theme-primary-a04',     `rgba(${rgb}, 0.04)`);
    r.style.setProperty('--theme-primary-a05',     `rgba(${rgb}, 0.05)`);
    r.style.setProperty('--theme-primary-a06',     `rgba(${rgb}, 0.06)`);
    r.style.setProperty('--theme-primary-a08',     `rgba(${rgb}, 0.08)`);
    r.style.setProperty('--theme-primary-a10',     `rgba(${rgb}, 0.10)`);
    r.style.setProperty('--theme-primary-a12',     `rgba(${rgb}, 0.12)`);
    r.style.setProperty('--theme-primary-a14',     `rgba(${rgb}, 0.14)`);
    r.style.setProperty('--theme-primary-a15',     `rgba(${rgb}, 0.15)`);
    r.style.setProperty('--theme-primary-a20',     `rgba(${rgb}, 0.20)`);
    r.style.setProperty('--theme-primary-a25',     `rgba(${rgb}, 0.25)`);
    r.style.setProperty('--theme-primary-a30',     `rgba(${rgb}, 0.30)`);
    r.style.setProperty('--theme-primary-a35',     `rgba(${rgb}, 0.35)`);
    r.style.setProperty('--theme-primary-a40',     `rgba(${rgb}, 0.40)`);
    r.style.setProperty('--theme-primary-a50',     `rgba(${rgb}, 0.50)`);
    r.style.setProperty('--theme-primary-a60',     `rgba(${rgb}, 0.60)`);
  }
}
